import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/admin" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white grain relative flex items-center justify-center px-6" data-testid="admin-login">
      <Link
        to="/"
        className="absolute top-6 left-6 text-[11px] uppercase tracking-[0.28em] text-white/50 hover:text-red-400 font-mono-ef"
      >
        ← Emergent.Food
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
        className="w-full max-w-md"
      >
        <div className="text-[11px] uppercase tracking-[0.28em] text-red-400 font-mono-ef mb-4">
          — Admin
        </div>
        <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight leading-none">
          Sign in.
        </h1>
        <p className="mt-3 text-white/50 text-sm">
          Access the internal Emergent Food operations dashboard.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-6" data-testid="admin-login-form">
          <label className="block">
            <span className="block font-mono-ef text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">
              Email <span className="text-red-400">*</span>
            </span>
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b border-white/15 focus:border-red-400 outline-none py-3 text-white placeholder:text-white/30 font-display text-lg transition-colors"
              autoComplete="email"
            />
          </label>

          <label className="block">
            <span className="block font-mono-ef text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">
              Password <span className="text-red-400">*</span>
            </span>
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-white/15 focus:border-red-400 outline-none py-3 text-white placeholder:text-white/30 font-display text-lg transition-colors"
              autoComplete="current-password"
            />
          </label>

          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="btn-pill btn-primary w-full disabled:opacity-50"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
